import { fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { ItemFieldRow } from './item-field-reader';
import { readItemField } from './item-field-reader';
import { readItemNumericSlot } from './enchantment-template';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';

/** Native existing-template size composition. The SELECT candidate lists keep
 * database order; absent compositions remain explicit save intents, never UUIDs
 * fabricated by a pure compile or writes from a definition preview. */
export function resolveNpcSizedTemplate(base: Row, item: string, existing: string, size: string,
    build: (id: string, passive: boolean) => ItemFieldRow | null,
    definition: (id: string) => Row | null, value: (row: ItemFieldRow | null, key: string) => unknown): string {
    const user = build(existing, false), sized = build(size, false);
    const layers = (row: ItemFieldRow | null): Row | null => {
        const raw = readItemField(row, 'layers_to_add');
        return raw !== null && typeof raw === 'object' ? Object.fromEntries(Object.entries(raw)) : null;
    };
    const userLayers = layers(user), sizeLayers = layers(sized);
    if (!userLayers || !sizeLayers) return existing;
    const list = (row: Row, key: string): string[] => Array.isArray(row[key]) ? row[key] as string[] : [];
    const chargedSpells = list(userLayers, 'charged_spell'), chargedAbilities = list(userLayers, 'charged_ability');
    const charged = chargedSpells.length + chargedAbilities.length > 0;
    const charges = value(user, 'item_charges');
    const maximum = charges == null || charges === '' ? null : phpJsonInt(readItemNumericSlot(charges));
    if (charged && (maximum === null || maximum < Math.max(0,
        ...chargedSpells.map(id => phpJsonInt(value(definition(id), 'spell_level'))),
        ...chargedAbilities.map(id => phpJsonInt(value(definition(id), 'ability_activation_charges')))))) return existing;
    const forms: Record<string, unknown> = { innate_spell_per_long_rest: list(userLayers, 'innate_spell_per_long_rest'),
        at_will_spell: list(userLayers, 'at_will_spell'), ...(charged ? { item_charges: maximum } : {}),
        charged_spell: chargedSpells, charged_ability: chargedAbilities,
        ...(charged ? { item_rechargeable: phpJsonTruthy(value(user, 'item_rechargeable')) } : {}),
        passive_ability: [...new Set([...list(userLayers, 'passive_ability'), ...list(sizeLayers, 'passive_ability')])] };
    if (Object.values(forms).some(values => Array.isArray(values) && values.some(id => !fromBaseUuid(id) || id !== id.toUpperCase()))) return existing;
    const candidates: string[] = [], allCandidates = fromBaseRecord(base.enchantment_match_candidates) ? base.enchantment_match_candidates : {};
    for (const [type, values] of Object.entries(forms)) if (Array.isArray(values)) for (const id of values) {
        const matches = allCandidates[`${type}:${id}`];
        if (!Array.isArray(matches)) throw new FromBaseInputMissing('size-composite-match-candidates', `${type}:${id}`);
        if (!matches.length) throw new FromBaseStageMissing('inventory-construction-grants', `static-size-composite-create:${item}`);
        candidates.push(...matches as string[]);
    }
    const expected = Object.values(forms).reduce<number>((n, values) => n + (Array.isArray(values) ? values.length : phpJsonTruthy(values) ? 1 : 0), 0);
    const rows = fromBaseRecord(base.enchantment_match_rows) ? base.enchantment_match_rows : {};
    for (const id of candidates) {
        const members = rows[id];
        if (!Array.isArray(members)) throw new FromBaseInputMissing('size-composite-match-rows', id);
        if (members.length !== expected) continue;
        if (Object.entries(forms).every(([type, values]) => !Array.isArray(values) || values.every(value => members.some(row =>
            readItemField(row, 'enchantment_type') === type && phpJsonString(readItemField(row, 'enchantment_item_id')).toUpperCase() === String(value).toUpperCase())))) return id;
    }
    throw new FromBaseStageMissing('inventory-construction-grants', `static-size-composite-create:${item}`);
}
